
import { CoordinatesModel, HourlyTempModel, HourlyWindModel, ThermicIndexParametersModel } from '../share/models/index';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { LocationWeatherResultModel } from '../share/models/locationWeatherResultModel';
import { DatePipe } from '@angular/common';
import { IDewPoint, IHourlyCloudCover, IRelativeHumidity } from '../share/interfaces';
import { Helper } from '../share/helper';
import { Constants } from '../share/constants';
import { WeatherApiCallParameters } from '../share/enums';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IDailyParameters } from '../share/interfaces/dailyParametersInterface';
import { environment } from 'src/environments/environment';


@Injectable({
  providedIn: 'root'
})
export class WeatherServiceApiService {

  constructor(private httpClient: HttpClient, private datePipe: DatePipe) { }

  getWeatherForLocation(location: CoordinatesModel,
    sDate: Date, selectedDate: string, weatherModel: string, forecastDuration: string): Observable<ThermicIndexParametersModel | undefined> {
    const params = this.createRequestParameters(location, selectedDate, weatherModel, forecastDuration);
    const timeNow = this.datePipe.transform(sDate, 'yyyy-MM-ddTHH:00');

    return this.httpClient
      .get<LocationWeatherResultModel>(Constants.WEATHER_API_ENDPOINT, { params })
      .pipe(
        map((result: LocationWeatherResultModel) => {
          const timeIndex = result.hourly.time.indexOf(timeNow);

          if (timeIndex !== -1) {
            const altitude850Temp = result.hourly.temperature_850hPa[timeIndex];
            const altitude700Temp = result.hourly.temperature_700hPa[timeIndex];
            const windSpeed850hPa = result.hourly.windspeed_850hPa[timeIndex];
            const hourlySurfaceT = result.hourly.temperature_2m[timeIndex];
            const cloudCover = result.hourly.cloudcover[timeIndex];
            const cloudCoverLow = result.hourly.cloud_cover_low[timeIndex];
            const cloudCoverMid = result.hourly.cloud_cover_mid[timeIndex];
            const cloudCoverHigh = result.hourly.cloud_cover_high[timeIndex];
            const windDirection850hPa = result.hourly.winddirection_850hPa[timeIndex];

            const dayIndex = result.daily.time.indexOf(this.datePipe.transform(sDate, 'yyyy-MM-dd'));
            const dailyParameters: IDailyParameters = {
              precipitationProbability: result.daily.precipitation_probability_max[0],
              maxTemp: result.daily.temperature_2m_max[dayIndex],
              date: result.daily.time[0],
              radarUrl: environment.mgm.radarUrl + '/' + location.radarPath,
            };
            const hourlyConvectiveCloudBase = result.hourly.convective_cloud_base[timeIndex];
            const hourlyCape = result.hourly.cape[timeIndex];

            const {
              hourlyTempModelList,
              hourlyWindModelList,
              hourlyCloudCoverModelList,
              hourlyRelativeHumidityModelList,
              hourlyDewPointModelList,
              hoursList
            } = this.getHourlyParameters(sDate, result, forecastDuration);

            const thermicIndexModel: ThermicIndexParametersModel = {
              altitude700T: altitude700Temp,
              altitude850T: altitude850Temp,
              availableAltitude: result.elevation,
              dailyParameters: dailyParameters,
              hourlyTempModelList: hourlyTempModelList,
              hourlyWindModelList: hourlyWindModelList,
              windDirection850hPa: windDirection850hPa,
              windSpeed850hPa: windSpeed850hPa,
              cloudCover: cloudCover,
              cloudCoverLow: cloudCoverLow,
              cloudCoverMid: cloudCoverMid,
              cloudCoverHigh: cloudCoverHigh,
              hourlyCloudCoverModelList: hourlyCloudCoverModelList,
              hourlyRelativeHumidityModelList: hourlyRelativeHumidityModelList,
              hourlySurfaceT: hourlySurfaceT,
              hourlyConvectiveCloudBase: hourlyConvectiveCloudBase,
              hourlyCape: hourlyCape,
              hoursList: hoursList,
              startDate: '',
              endDate: '',
              hourlyTemperature_2m: [],
              hourlyDewPoint: hourlyDewPointModelList
            };

            return thermicIndexModel;
          }

          return undefined;
        }),
        catchError((error) => {
          console.error('Error fetching weather data:', error);
          return of(undefined);
        })
      );
  }

  createRequestParameters(location: CoordinatesModel, selectedDate: string, weatherModel: string, forecastDuration: string): HttpParams {
    return new HttpParams().set(WeatherApiCallParameters.lat, location.latitude.toString())
      .set(WeatherApiCallParameters.long, location.longitude.toString())
      .set(WeatherApiCallParameters.daily,
        WeatherApiCallParameters.tempMax + ','
        + WeatherApiCallParameters.precipitationProbabilityMax)
      .set(WeatherApiCallParameters.hourly,
        WeatherApiCallParameters.temp700hPa + ','
        + WeatherApiCallParameters.temp850hPa + ','
        + WeatherApiCallParameters.wind850hPaDirection + ','
        + WeatherApiCallParameters.wind850hPaSpeed + ','
        + WeatherApiCallParameters.wind900hPaSpeed + ','
        + WeatherApiCallParameters.wind900hPaDirection + ','
        + WeatherApiCallParameters.windSpeed925hPa + ','
        + WeatherApiCallParameters.wind700hPaDirection + ','
        + WeatherApiCallParameters.windSpeed700hPa + ','
        + WeatherApiCallParameters.wind925hPaDirection + ','
        + WeatherApiCallParameters.cloudCover + ','
        + WeatherApiCallParameters.cloud_cover_low + ','
        + WeatherApiCallParameters.cloud_cover_mid + ','
        + WeatherApiCallParameters.cloud_cover_high + ','
        + WeatherApiCallParameters.relativeHumidity_850hPa + ','
        + WeatherApiCallParameters.relativeHumidity_700hPa + ','
        + WeatherApiCallParameters.temperature_2m + ','
        + WeatherApiCallParameters.convective_cloud_base + ','
        + WeatherApiCallParameters.cape + ','
        + WeatherApiCallParameters.windGusts10m + ','
        + WeatherApiCallParameters.precipitation + ','
        + WeatherApiCallParameters.precipitation_type + ','
        + WeatherApiCallParameters.dewPoint1000hPa + ','
        + WeatherApiCallParameters.dewPoint900hPa + ','
        + WeatherApiCallParameters.dewPoint850hPa + ','
        + WeatherApiCallParameters.dewPoint700hPa + ','
      )
      .set(WeatherApiCallParameters.startDate, selectedDate)
      .set(WeatherApiCallParameters.endDate, selectedDate)
      .set(WeatherApiCallParameters.timeZone, WeatherApiCallParameters.turkeyTimeZone)
      .set(WeatherApiCallParameters.models, weatherModel)
      .set(WeatherApiCallParameters.elevation, location.elevation.toString());
  }

  private getHourlyParameters(newDate: Date, result: LocationWeatherResultModel, forecastDuration: string): {
    hourlyTempModelList: HourlyTempModel[];
    hourlyWindModelList: HourlyWindModel[];
    hourlyCloudCoverModelList: IHourlyCloudCover[];
    hourlyRelativeHumidityModelList: IRelativeHumidity[];
    hourlyDewPointModelList: IDewPoint[];
    hoursList: string[];
  } {
    const hourlyTempModelList: HourlyTempModel[] = [];
    const hourlyWindModelList: HourlyWindModel[] = [];
    const hourlyCloudCoverModelList: IHourlyCloudCover[] = [];
    const hourlyRelativeHumidityModelList: IRelativeHumidity[] = [];
    const hourlyDewPointModelList: IDewPoint[] = [];
    const beginningTime = new Date(this.datePipe.transform(newDate, forecastDuration === '24' ? 'yyyy-MM-ddT00:00' : 'yyyy-MM-ddT06:00')).getTime();
    const endTime = new Date(this.datePipe.transform(newDate, forecastDuration === '24' ? 'yyyy-MM-ddT23:00' : 'yyyy-MM-ddT19:00')).getTime();
    const hoursList: string[] = [];
    result.hourly.time.forEach(t => {
      let aTime = new Date(t).getTime();
      if (aTime >= beginningTime && aTime <= endTime) {
        let hour = this.datePipe.transform(t, 'HH:mm');
        hoursList.push(hour);
        let tIndex = result.hourly.time.indexOf(t);
        let hourlyAltitude850Temp = result.hourly.temperature_850hPa[tIndex];
        let hourlyAltitude700Temp = result.hourly.temperature_700hPa[tIndex];
        let hourlySurfaceTemp = result.hourly.temperature_2m[tIndex];

        let hourlyModel = new HourlyTempModel(hourlyAltitude850Temp, hourlyAltitude700Temp, this.datePipe.transform(t, 'HH:mm'),
          hourlySurfaceTemp);
        hourlyTempModelList.push(hourlyModel);

        let hourlyWindSpeed850 = result.hourly.windspeed_850hPa[tIndex];
        let hourlyWindDirection850 = result.hourly.winddirection_850hPa[tIndex];
        let hourlyWindSpeed900 = result.hourly.windspeed_900hPa[tIndex];
        let hourlyWindDirection900 = result.hourly.winddirection_900hPa[tIndex];
        let hourlyWindSpeed925 = result.hourly.wind_speed_925hPa[tIndex];
        let hourlyWindDirection925 = result.hourly.winddirection_925hPa[tIndex];
        let hourlyWindSpeed700 = result.hourly.wind_speed_700hPa[tIndex];
        let hourlyWindDirection700 = result.hourly.winddirection_700hPa[tIndex];
        let hourlyWindGusts10m: number = result.hourly.wind_gusts_10m[tIndex];

        let hourlyWindModel = new HourlyWindModel({
          windSpeed_850hPa: hourlyWindSpeed850,
          windDirection_850hPa: hourlyWindDirection850,
          hour: hour,
          windDirectionName850hPa: Helper.getWindDirectionText(hourlyWindDirection850),
          speedTextCss850: Helper.getSpeedTextCss(hourlyWindSpeed850),

          windDirection_900hPa: hourlyWindDirection900,
          windSpeed_900hPa: hourlyWindSpeed900,
          windDirectionName900hPa: Helper.getWindDirectionText(hourlyWindDirection900),
          speedTextCss900: Helper.getSpeedTextCss(hourlyWindSpeed900),
          windDirection_700hPa: hourlyWindDirection700,
          windSpeed_700hPa: hourlyWindSpeed700,
          windDirectionName700hPa: Helper.getWindDirectionText(hourlyWindDirection700),
          speedTextCss700: Helper.getSpeedTextCss(hourlyWindSpeed700),
          windDirection_925hPa: hourlyWindDirection925,
          windSpeed_925hPa: hourlyWindSpeed925,
          windDirectionName925hPa: Helper.getWindDirectionText(hourlyWindDirection925),
          speedTextCss925: Helper.getSpeedTextCss(hourlyWindSpeed925),
          windGusts10m: hourlyWindGusts10m,
          gustsTextCss10m: Helper.getSpeedTextCss(hourlyWindGusts10m > hourlyWindSpeed850 ? hourlyWindGusts10m * 2 : hourlyWindGusts10m),
        });

        hourlyWindModelList.push(hourlyWindModel);

        let hourlyCloudCover = result.hourly.cloudcover[tIndex];
        let hourlyCloudCoverLow = result.hourly.cloud_cover_low[tIndex];
        let hourlyCloudCoverMid = result.hourly.cloud_cover_mid[tIndex];
        let hourlyCloudCoverHigh = result.hourly.cloud_cover_high[tIndex];
        let hourlyConvectiveCloudBase = result.hourly.convective_cloud_base[tIndex];
        let hourlyCape = result.hourly.cape[tIndex];
        let hourlyCloudCoverModel = {
          cloudCover: hourlyCloudCover,
          hour: this.datePipe.transform(t, 'HH:mm'),
          hourlyConvectiveCloudBase: hourlyConvectiveCloudBase,
          cloudCoverLow: hourlyCloudCoverLow,
          cloudCoverMid: hourlyCloudCoverMid,
          cloudCoverHigh: hourlyCloudCoverHigh,
          hourlyCape: hourlyCape,
          precipitation: result.hourly.precipitation[tIndex],
          precipitation_type: result.hourly.precipitation_type[tIndex]
        } as IHourlyCloudCover;

        hourlyCloudCoverModelList.push(hourlyCloudCoverModel);

        let hourlyRelativeHumidity850hPa = result.hourly.relativehumidity_850hPa[tIndex];
        let hourlyRelativeHumidity700hPa = result.hourly.relativehumidity_700hPa[tIndex];

        let hourlyRelativeHumidityModel: IRelativeHumidity = {
          relativeHumidity850hPa: hourlyRelativeHumidity850hPa,
          relativeHumidity_700hPa: hourlyRelativeHumidity700hPa,
          hour: this.datePipe.transform(t, 'HH:mm')
        }
        hourlyRelativeHumidityModelList.push(hourlyRelativeHumidityModel);

        hourlyDewPointModelList.push({
          dewPoint1000hPa: result.hourly.dew_point_1000hPa[tIndex],
          dewPoint900hPa: result.hourly.dew_point_900hPa[tIndex],
          dewPoint850hPa: result.hourly.dew_point_850hPa[tIndex],
          dewPoint700hPa: result.hourly.dew_point_700hPa[tIndex],
          hour: this.datePipe.transform(t, 'HH:mm')
        } as IDewPoint);


      }
    });
    return {
      hourlyTempModelList, hourlyWindModelList, hourlyCloudCoverModelList, hourlyRelativeHumidityModelList, hourlyDewPointModelList,
      hoursList
    };
  }
}
